import React, { useCallback, useState } from "react";
import {
  Accordion,
  Button,
  Checkbox,
  Flex,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Text,
} from "@jsq/ds/core";
import {
  packageTitle,
  type EffectiveDoc,
  type PackageGroup,
} from "#utils/classificationGrouping";
import { ReviewRow } from "../ReviewRow";
import * as styles from "./styles_vanilla.css";

export type PackageGroupCardProps = {
  group: PackageGroup;
  onAssign: (doc: EffectiveDoc) => void;
  onBulkAssign: (docs: EffectiveDoc[]) => void;
  onMove: (doc: EffectiveDoc) => void;
  onEditDocType: (doc: EffectiveDoc) => void;
  onRemove: (doc: EffectiveDoc) => void;
  disabled?: boolean;
};

/**
 * PackageGroupCard — one collapsible deliverable-package card on the
 * classification confirm screen, rendering a single `PackageGroup`. The pinned
 * "Not classified" bucket renders with an error status, per-row selection
 * checkboxes, and a header "Assign documents" action for bulk classification;
 * classified packages show their file count.
 */
export const PackageGroupCard: React.FC<PackageGroupCardProps> = ({
  group,
  onAssign,
  onBulkAssign,
  onMove,
  onEditDocType,
  onRemove,
  disabled,
}) => {
  const fileCount = group.docs.length;
  const selectable = group.unclassified;

  // Selection starts empty: pre-selecting every doc meant users opened the
  // assign modal already holding a full selection they hadn't chosen, and bulk
  // assigning it was destructive and expensive to undo.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const toggleSelect = useCallback((doc: EffectiveDoc) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(doc.id)) {
        next.delete(doc.id);
      } else {
        next.add(doc.id);
      }
      return next;
    });
  }, []);

  const allSelected =
    selectable && fileCount > 0 && group.docs.every((d) => selectedIds.has(d.id));
  const someSelected = selectable && group.docs.some((d) => selectedIds.has(d.id));

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const all = group.docs.length > 0 && group.docs.every((d) => prev.has(d.id));
      return all ? new Set() : new Set(group.docs.map((d) => d.id));
    });
  }, [group.docs]);

  const selectedDocs = group.docs.filter((d) => selectedIds.has(d.id));

  return (
    // The DS Accordion ships a global `accordion + accordion { border-top: 0 }`
    // rule that dedupes borders when accordions stack flush. These cards render
    // as gap-separated siblings, so that rule stripped the top border of every
    // card after the first; the wrapper breaks the sibling adjacency so each
    // card keeps its full border.
    <div>
      <Accordion
        title={
          group.unclassified ? "Not classified to deliverable package" : packageTitle(group)
        }
        statusText={group.unclassified ? "Needs classification" : undefined}
        statusVariant={group.unclassified ? "error" : undefined}
        statusIconType={group.unclassified ? "exclamation-triangle" : undefined}
        subtitle={
          group.unclassified ? undefined : `${fileCount} file${fileCount !== 1 ? "s" : ""}`
        }
        startExpanded
        disableBodyPadding
        data-group-id={group.key}
      >
        <Table variant="neutral" condensed>
          <TableBody>
            {selectable && (
              <TableRow>
                <TableCell className={styles.selectCell}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={toggleAll}
                    disabled={disabled}
                    aria-label="Select all"
                  />
                </TableCell>
                <TableCell fill>
                  <Text asSpan variant="secondary-sm">
                    {selectedDocs.length} of {fileCount} selected
                  </Text>
                </TableCell>
                <TableCell rightAligned nowrap>
                  <Flex justifyContent="flex-end">
                    <Button
                      variant="primary"
                      isInline
                      disabled={disabled || !someSelected}
                      onClick={() => onBulkAssign(selectedDocs)}
                    >
                      Assign documents
                    </Button>
                  </Flex>
                </TableCell>
              </TableRow>
            )}
            {group.docs.map((doc) => (
              <ReviewRow
                key={doc.id}
                doc={doc}
                needsClassify={group.unclassified}
                selectable={selectable}
                selected={selectedIds.has(doc.id)}
                onToggleSelect={toggleSelect}
                onAssign={onAssign}
                onMove={onMove}
                onEditDocType={onEditDocType}
                onRemove={onRemove}
                disabled={disabled}
              />
            ))}
          </TableBody>
        </Table>
      </Accordion>
    </div>
  );
};
